import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormEvent, useState } from 'react';

interface Props {
  onSubmit: (email: string) => Promise<void>;
  onBackToLogin: () => void;
  loading: boolean;
  error: string;
  onErrorClear: () => void;
}

export const ResetPasswordForm = ({
  onSubmit,
  onBackToLogin,
  loading,
  error,
  onErrorClear,
}: Props) => {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');

  const validateForm = (): boolean => {
    if (!email) {
      setValidationError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setValidationError('Please enter a valid email');
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await onSubmit(email);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3} sx={{ mt: 1 }}>
        <Typography color="text.secondary">
          If you have an account, we'll send you a link to reset your password.
        </Typography>

        <TextField
          data-qaid="reset-password-email-input"
          label="Email Address"
          type="email"
          required
          fullWidth
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          error={!!validationError}
          helperText={validationError}
        />

        {error && (
          <Alert severity="error" onClose={onErrorClear}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          data-qaid="reset-password-button"
          variant="contained"
          disabled={loading || !email}
          size="large"
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            data-qaid="back-to-login-button"
            onClick={onBackToLogin}
            variant="text"
            size="small"
          >
            Back to Login
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
